<template>
  <div class="clap-component" v-if="possedeClapVideo">
    <h2 class="title">{{ $t('posts.clap.title') }}</h2>

    <!-- Actions buttons -->
    <div class="clap-actions">
      <!-- Si vidéo disponible -->
      <div v-if="getHasUrlClapVideo">
        <div class="button-group">
          <el-button @click="openUrlClap" class="mb-3">{{ $t('posts.clap.downloadClapButton') }}</el-button>
          <el-button
              type="success"
              @click="copyVideoLink"
              class="mb-3"
              :class="{ 'copied': isLinkCopied }"
          >
            <i v-if="!isLinkCopied" class="el-icon-copy-document"></i>
            <i v-else class="el-icon-check copied-icon"></i>
            {{ isLinkCopied ? 'Copié !' : 'Copier le lien Flow Vidéo' }}
          </el-button>
        </div>
      </div>
      <!-- Si pas de vidéo disponible -->
      <div v-else>
        <!-- En cours de génération -->
        <div v-if="getClapInProgressByPost === true">
          <p v-html="$t('posts.clap.patientTextBegin')"></p>
          <el-button @click="checkUrlClap" class="mb-3">{{ $t('posts.clap.checkUrlClapButton') }}</el-button>
          <p v-html="$t('posts.clap.patientTextEnd')"></p>
        </div>
        <!-- Prêt à générer -->
        <div v-else>
          <el-button type="primary" @click="clickGenerateClap" class="mb-3">
            {{ $t('posts.clap.generateClapButton') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- Section principale avec quota et boutons -->
    <div class="clap-content">
      <!-- Informations quota -->
      <div class="quota-info">
        <h4>{{ $t('posts.clap.informationTitle') }}</h4>
        <p>
          <strong>{{ $t('posts.clap.forfaitClapTitle') }} : </strong>
          {{ $t('posts.clap.forfaitClap', { 'quotaMax': getQuotaMax, 'nbClapRestants': getNbClapRestants }) }}
        </p>
        <p>
          <strong>{{ $t('posts.clap.monitoringClapTitle') }} :</strong>
          {{ $t('posts.clap.monitoringClap', { 'nbClapGeneres': getNbClapGeneres, 'nbClapRestants': getNbClapRestants }) }}
          <el-tooltip placement="top-start" :content="$t('posts.clap.monitoringClapTooltip')">
            <em class="el-icon-question"></em>
          </el-tooltip>
        </p>
      </div>
    </div>

  </div>
  <!-- Message commercial - si pas de clapVideo -->
  <div v-else class="container-message-commercial">
    <div class="box-commercial">
      <p>{{ $t('posts.networks.commercialText1') }}{{
          $t('posts.networks.commercialText1Bis')}}</p>
      <p>{{ $t('posts.networks.commercialText2') }}</p>
      <div class="button-commercial">
        <el-button type="primary" v-if="!isSubUser" @click="contactTeam"> {{ $t('socialMedia.buttonContactTeam') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { generateAutoclap } from '@/services/clap'
import { getPostById } from '@/services/posts'

export default {
  name: 'ClapComponent',
  props: {
    post: {
      type: Object,
      required: true
    },
    possedeClapVideo: {
      type: Boolean,
      default: false
    },
    universe: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isLinkCopied: false
    }
  },
  computed: {
    ...mapGetters('storeClap', [
      'getHasUrlClap',
      'getQuotaMax',
      'getNbClapRestants',
      'getNbClapGeneres'
    ]),
    ...mapGetters(['isSubUser']),
    
    getHasUrlClapVideo() {
      // Détecter l'URL dans les deux formats possibles (comme dans votre version originale)
      const urlDirect = this.post.url_clap
      const urlDonnees = this.post.donnees && this.post.donnees.url_clap && this.post.donnees.url_clap.valeur
      
      return Boolean(urlDirect || urlDonnees)
    },
    
    getClapInProgressByPost() {
      return this.$store.getters['storeClap/getClapInProgressByPost'](this.post.id)
    }
  },
  methods: {
    clickGenerateClap() {
      // Message de lancement
      this.$message({
        message: this.$t('posts.clap.msgClapLaunch'),
        type: 'info',
        duration: 5000
      })
      
      // Marquer ce post comme en cours de génération
      this.$store.commit('storeClap/setClapInProgressByPost', {
        postId: this.post.id,
        inProgress: true
      })
      
      generateAutoclap(this.post)
        .then((response) => {
          if (response.type === 'error') {
            this.$message({
              message: response.message,
              type: 'error',
              duration: 10000
            })
            // Arrêter l'état "en cours" en cas d'erreur
            this.$store.commit('storeClap/setClapInProgressByPost', {
              postId: this.post.id,
              inProgress: false
            })
          } else {
            // Message de progression
            this.$message({
              message: this.$t('posts.clap.msgClapInProgress'),
              type: 'info',
              duration: 5000
            })
            
            // Mettre à jour le quota après génération
            this.$store.dispatch('storeClap/checkQuotaClap', {
              universe: this.universe,
              possedeClap: this.possedeClapVideo
            })

            // Émettre l'événement de génération lancée
            this.$emit('clap-generation-started')
          }
        })
        .catch((error) => {
          console.error('Erreur lors de la génération du clap:', error)
          this.$message({
            message: 'Erreur lors de la génération de la vidéo',
            type: 'error',
            duration: 10000
          })
          // Arrêter l'état "en cours" en cas d'erreur
          this.$store.commit('storeClap/setClapInProgressByPost', {
            postId: this.post.id,
            inProgress: false
          })
        })
    },

    checkUrlClap() {
      // Message de patience pour le rafraîchissement
      this.$message({
        message: this.$t('posts.clap.patientTextBegin'),
        type: 'info',
        duration: 3000
      })
      
      getPostById(this.post.id)
        .then((post) => {
          const url = post.url_clap || (post.donnees && post.donnees.url_clap && post.donnees.url_clap.valeur)
          if (url) {
            this.$message({
              message: this.$t('posts.clap.msgClapEnded'),
              type: 'success',
              duration: 5000
            })
            
            // Mettre à jour le post local pour déclencher la réactivité
            this.$emit('update-post', post)
            
            // Mettre à jour le store
            this.$store.commit('storeClap/setUrlClap', {
              id: post.id,
              url_clap: url
            })
            
            // Arrêter l'état "en cours" pour ce post
            this.$store.commit('storeClap/setClapInProgressByPost', {
              postId: this.post.id,
              inProgress: false
            })
            
            // Émettre l'événement pour que le parent puisse réagir
            this.$emit('clap-ready', url)
          } else {
            // Pour le rafraîchissement, utiliser patientTextEnd au lieu de msgClapInProgress
            this.$message({
              message: this.$t('posts.clap.patientTextEnd'),
              type: 'info',
              duration: 5000
            })
          }
        })
        .catch((error) => {
          console.error('Erreur lors de la vérification du clap:', error)
          this.$message({
            message: 'Erreur lors de la vérification du statut de la vidéo',
            type: 'error',
            duration: 5000
          })
        })
    },

    openUrlClap() {
      const url = this.post.url_clap || (this.post.donnees && this.post.donnees.url_clap && this.post.donnees.url_clap.valeur)
      if (url) {
        window.open(url, '_blank')
      }
    },

    copyVideoLink() {
      const url = this.post.url_clap || (this.post.donnees && this.post.donnees.url_clap && this.post.donnees.url_clap.valeur)
      if (url) {
        navigator.clipboard.writeText(url).then(() => {
          this.isLinkCopied = true
          this.$message({
            message: 'Lien Flow Vidéo copié dans le presse-papier !',
            type: 'success',
            duration: 3000
          })

          this.$emit('clap-link-copied', url)

          setTimeout(() => {
            this.isLinkCopied = false
          }, 3000)
        }).catch(() => {
          this.$message({
            message: 'Erreur lors de la copie du lien',
            type: 'error',
            duration: 3000
          })
        })
      }
    },
    
    contactTeam() {
      window.open('https://www.ubiflow.net/annonces-immobilieres-google-facebook-instagram')
    }
  },
  
  mounted() {
    // Vérifier le quota au montage du composant
    if (this.possedeClapVideo) {
      this.$store.dispatch('storeClap/checkQuotaClap', {
        universe: this.universe,
        possedeClap: this.possedeClapVideo
      })
    }

    // Initialiser l'URL du clap si elle existe déjà
    const url = this.post.url_clap || (this.post.donnees && this.post.donnees.url_clap && this.post.donnees.url_clap.valeur)
    if (url) {
      this.$store.commit('storeClap/setUrlClap', {
        id: this.post.id,
        url_clap: url
      })
      // S'assurer que l'état "en cours" est arrêté si l'URL existe déjà
      this.$store.commit('storeClap/setClapInProgressByPost', {
        postId: this.post.id,
        inProgress: false
      })
    }

    if (this.post) {
      this.$store.commit('storeClap/setPostStatus', this.post)
    }
  }
}
</script>

<style lang="scss" scoped>
.clap-component {
  .title {
    color: #1a4a56;
    font-weight: 600;
    font-size: 15px;
    margin-top: 15px;
    margin-bottom: 20px;
  }

  .clap-actions {
    .button-group {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .mb-3 {
      margin-bottom: 15px;
    }

    .copied {
      .copied-icon {
        animation: checkAnimation 0.3s ease-in-out;
      }
    }

    p {
      margin: 10px 0;
      line-height: 1.4;
    }
  }

  .clap-content {
    .quota-info {
      margin-bottom: 20px;

      h4 {
        margin-bottom: 10px;
        font-weight: 600;
      }

      p {
        margin-bottom: 8px;
        line-height: 1.4;

        strong {
          color: #1a4a56;
        }
      }
    }
  }
}

@keyframes checkAnimation {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
</style>
